/**
 * AdminInspectionTemplates — per-Company trade templates for InspectRadar Grid.
 *
 * Rows = room types (bedroom, bathroom, kitchen, etc.).
 * Columns = every trade in the catalog.
 * A checked cell means "this room type should ask about this trade".
 * Overall is always on and locked (every row rates the Room overall).
 */
import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowClockwise, FloppyDisk, CheckSquare, Square } from "@phosphor-icons/react";

const ROOM_LABELS = {
  bedroom: "Bedroom",
  bathroom: "Bathroom",
  half_bath: "Half Bath",
  kitchen: "Kitchen",
  living_room: "Living Room",
  dining: "Dining",
  hallway: "Hallway",
  closet: "Closet / Pantry",
  laundry: "Laundry",
  garage: "Garage",
  basement: "Basement",
  office: "Office",
  patio: "Patio / Deck",
  other: "Other (fallback)",
};

export default function AdminInspectionTemplates() {
  const [catalog, setCatalog] = useState({});
  const [templates, setTemplates] = useState({});
  const [original, setOriginal] = useState({});
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const [r, p] = await Promise.all([
        api.get("/settings/inspection-trade-templates"),
        api.get("/settings/inspection-trade-templates/preview"),
      ]);
      setCatalog(r.data.catalog || {});
      setTemplates(r.data.templates || {});
      setOriginal(JSON.parse(JSON.stringify(r.data.templates || {})));
      setPreview(p.data.preview || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load templates");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const roomTypes = useMemo(() => Object.keys(ROOM_LABELS).filter(k => templates[k]), [templates]);
  const tradeKeys = useMemo(() => Object.keys(catalog).filter(k => k !== "overall"), [catalog]);

  const isChecked = (roomType, trade) => (templates[roomType] || []).includes(trade);

  const toggle = (roomType, trade) => {
    setTemplates(prev => {
      const list = new Set(prev[roomType] || []);
      if (list.has(trade)) list.delete(trade); else list.add(trade);
      // Keep order consistent with catalog
      const ordered = ["overall", ...Object.keys(catalog).filter(k => k !== "overall" && list.has(k))];
      return { ...prev, [roomType]: ordered };
    });
  };

  const dirty = useMemo(() => JSON.stringify(templates) !== JSON.stringify(original), [templates, original]);

  const save = async () => {
    setSaving(true);
    try {
      // Strip overall before sending; backend force-prepends it on read.
      const toSend = Object.fromEntries(roomTypes.map(rt => [rt, (templates[rt] || []).filter(t => t !== "overall")]));
      await api.put("/settings/inspection-trade-templates", { templates: toSend });
      toast.success("Templates saved");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!confirm("Reset every room type back to the default template?")) return;
    try {
      await api.post("/settings/inspection-trade-templates/reset");
      toast.success("Templates reset to defaults");
      await load();
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="insp-templates-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-[#0B1F3A] tracking-tight">InspectRadar · Trade Templates</h1>
            <div className="text-xs text-slate-500 mt-1 max-w-2xl">
              Pick which trades each room type asks about in the Grid.
              A checked cell = that room type shows that trade column in the Grid; unchecked = the cell renders as "—" and skips.
              <br/>
              <b>Overall</b> is always on and can't be turned off.
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={resetDefaults} data-testid="tpl-reset-btn"
              className="h-9 px-3 bg-white border-2 border-slate-200 hover:border-slate-400 text-slate-700 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-1">
              <ArrowClockwise weight="bold" size={14}/>Reset
            </button>
            <button onClick={save} disabled={!dirty || saving} data-testid="tpl-save-btn"
              className="h-9 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-40 text-white font-black text-xs uppercase tracking-widest inline-flex items-center gap-1">
              <FloppyDisk weight="bold" size={14}/>{saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {busy ? (
          <div className="text-center py-12 text-slate-400">Loading…</div>
        ) : (
          <>
            {/* Matrix */}
            <div className="overflow-x-auto border-2 border-slate-200 rounded-lg bg-white">
              <table className="w-full text-xs" data-testid="tpl-matrix">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="sticky left-0 bg-slate-100 text-left px-3 py-2 border-b-2 border-slate-200 min-w-[160px]">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Room Type</span>
                    </th>
                    {tradeKeys.map(t => (
                      <th key={t} className="px-1 py-2 border-b-2 border-slate-200 text-center min-w-[64px]">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-700">
                          {catalog[t]?.short}
                        </div>
                        <div className="text-[9px] text-slate-500 normal-case font-medium">
                          {catalog[t]?.label}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {roomTypes.map((rt, i) => (
                    <tr key={rt} className={i % 2 ? "bg-slate-50/50" : "bg-white"}>
                      <td className="sticky left-0 bg-inherit px-3 py-1.5 border-b border-slate-100 font-bold text-[#0B1F3A]"
                          data-testid={`tpl-row-${rt}`}>
                        {ROOM_LABELS[rt] || rt}
                        <div className="text-[9px] text-slate-400 font-normal normal-case font-mono">{rt}</div>
                      </td>
                      {tradeKeys.map(t => {
                        const on = isChecked(rt, t);
                        return (
                          <td key={t} className="px-1 py-1 border-b border-slate-100 text-center">
                            <button onClick={() => toggle(rt, t)}
                              data-testid={`tpl-cell-${rt}-${t}`}
                              aria-label={`${on ? "Remove" : "Add"} ${catalog[t]?.label} for ${ROOM_LABELS[rt]}`}
                              className={`h-8 w-8 inline-flex items-center justify-center rounded border-2 transition-all hover:scale-110 ${
                                on ? "bg-fuchsia-600 border-fuchsia-700 text-white" : "bg-white border-slate-300 text-slate-300 hover:border-fuchsia-400"
                              }`}>
                              {on ? <CheckSquare weight="fill" size={16}/> : <Square weight="bold" size={16}/>}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Preview */}
            <div>
              <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2">
                Preview · how common room names classify
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {preview.map(p => (
                  <div key={p.room_name} className="border-2 border-slate-200 bg-white rounded-lg p-2"
                       data-testid={`tpl-preview-${p.room_type}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-sm text-[#0B1F3A]">{p.room_name}</span>
                      <span className="text-[9px] font-black uppercase tracking-widest text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded font-mono">
                        {p.room_type}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {p.trades.map(t => (
                        <span key={t} className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          t === "overall" ? "bg-[#0f172a] text-white" : "bg-slate-100 text-slate-700"
                        }`}>
                          {catalog[t]?.short || t}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}
